import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, inject } from '@angular/core';
import { PricingService } from 'src/app/services/pricing.service';
import { StoryblokService } from 'src/app/services/storyblok.service';
import { StickyComponent } from 'src/app/shared-extra/components/sticky/sticky.component';
import { StickyContainerDirective } from 'src/app/shared-extra/directives/sticky-container.directive';
import { ResizeDirective } from 'src/app/shared/directives/resize.directive';
import { TooltipDirective } from 'src/app/shared/directives/tooltip.directive';
import { CustomCurrencyPipe } from 'src/app/shared/pipes/custom-currency.pipe';
import { ProductPrice } from 'src/app/types/product.type';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { StoryblokCdnPipe } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokRichTextPipe } from '../../storyblok-rich-text.pipe';
import { StoryblokDirective } from '../../storyblok.directive';
import { BlockStyle, getPageWidth } from '../block-wrapper/block-wrapper.component';

@Component({
  selector: 'app-product-comparison-list',
  standalone: true,
  templateUrl: './product-comparison-list.component.html',
  styleUrls: ['./product-comparison-list.component.scss'],
  imports: [
    CommonModule,
    StoryblokDirective,
    ResizeDirective,
    CustomCurrencyPipe,
    StickyComponent,
    StickyContainerDirective,
    TooltipDirective,
    SvgComponent,
    StoryblokRichTextPipe,
    StoryblokCdnPipe,
  ],
})
export class ProductComparisonListComponent implements OnChanges {
  private storyblokService = inject(StoryblokService);
  private pricingService = inject(PricingService);
  @Input() _uid: string; // just here to prevent errors
  @Input() component: string; // just here to prevent errors
  @Input() _editable: string;
  @Input() replacer?: (key: string) => string;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('style') _style: BlockStyle[];
  style: BlockStyle;
  screens: string[] = ['mobile', 'tablet', 'desktop'];
  @Input() products: string[];
  @Input() title = 'Compare';

  @Input() previewFeatureGroups: { title: string; features: unknown[] }[];

  stickyHeader = true; // TODO: make this configurable in storyblok

  pageWidth: number;

  featureGroups: {
    title: string;
    features: {
      uuid: string;
      new: boolean;
      text: string;
      title: string;
      comingSoon: boolean;
      image?: { filename: string };
      _editable?: string;
    }[];
  }[] = [];

  comparisons: {
    name: string;
    price: ProductPrice;
    color: string;
    _editable?: string;
    features: {
      [id: string]: {
        id: string;
        value: boolean;
        customTextValue?: string;
      };
    };
  }[] = [];

  ngOnChanges(): void {
    this.style = this._style[0] || <BlockStyle>{};
    this.pageWidth = getPageWidth(this.style.pageWidth, this.style.customPageWidth);

    this.screens = [];
    if (!this.style?.hideOnMobile) this.screens.push('mobile');
    if (!this.style?.hideOnTablet) this.screens.push('tablet');
    if (!this.style?.hideOnDesktop) this.screens.push('desktop');

    const backendPrices = this.storyblokService.getGlobalData('backendPrices');
    const relationships = this.storyblokService.getGlobalData('relationships');

    this.featureGroups = [];

    this.products.forEach(id => {
      const data = relationships.find(rel => rel.uuid === id);

      (this.previewFeatureGroups || data.content.featureGroups).forEach(group => {
        let featureGroup = this.featureGroups.find(g => g.title === group.title);
        if (!featureGroup) {
          featureGroup = {
            title: group.title,
            features: [],
          };
          this.featureGroups.push(featureGroup);
        }
        group.features.forEach(feature => {
          if (featureGroup.features.some(f => f.uuid === feature.feature)) return;
          featureGroup.features.push({
            ...relationships.find(rel => rel.content.component === 'ProductFeature' && rel.uuid === feature.feature)
              ?.content,
            uuid: feature.feature,
          });
        });
      });
    });

    this.comparisons = this.products.map(id => {
      const data = relationships.find(box => box.uuid === id);

      const productLink = relationships.find(
        rel => rel.content.component === 'PricingTechnicalLink' && rel.uuid === data.content.product
      );

      const price = this.pricingService.convertPrice(
        backendPrices.find(price => productLink.content.lookupKeyYearly === price.lookup_key)
      );

      const features = {};
      (this.previewFeatureGroups || data.content.featureGroups).forEach(group => {
        group.features.forEach(feature => (features[feature.feature] = feature));
      });

      return {
        name: data.name,
        price: price,
        color: productLink.content.color,
        features,
      };
    });
  }
}
